<template>
  <div class="stock-view-page stock-view-invoice">
    <StockDocumentPageHeading
      :title="$t('STOCK.INVOICES.PAGE_TITLE')"
      :code="isNew || !isDocumentReady ? '' : document.code"
      :new-label="isNew ? $t('STOCK.INVOICES.NEW') : ''"
    />

    <div v-if="!isDocumentReady" class="stock-invoice-loading">
      <v-progress-circular
        v-if="!documentLoadFailed"
        indeterminate
        color="teal"
        size="56"
        width="4"
      ></v-progress-circular>
      <v-icon v-else color="error" size="56">mdi-alert-circle-outline</v-icon>
      <div class="stock-invoice-loading__label">
        {{ documentLoadFailed ? $t("STOCK.INVOICES.LOAD_FAILED") : $t("STOCK.INVOICES.LOADING") }}
      </div>
    </div>

    <template v-else>
    <StockInvoiceHero
      :code="displayDocumentCode"
      :internal-code="document.code"
      :fiscal-number="document.fiscal_number"
      :status="isNew ? 'draft' : document.status"
      :type="document.type"
      :direction="document.direction"
      :company-name="companyName"
      :external-number="document.external_number"
      :issue-date="document.issue_date"
      :is-locked="document.is_locked"
      :is-new="isNew"
    >
      <template v-if="hasHeroActions" v-slot:actions>
        <div v-if="canSave || canIssue" class="stock-invoice-hero__action-row">
          <v-btn v-if="canSave" color="white" class="black--text" small :disabled="isProcessing" @click="save">
            {{ $t("GENERAL.LABELS.SAVE") }}
          </v-btn>
          <v-btn
            v-if="canIssue"
            color="#c8e6c9"
            class="green--text text--darken-4"
            small
            :disabled="isProcessing"
            @click="issueInvoice"
          >
            <v-icon left small>mdi-check-decagram</v-icon>
            {{ issueActionLabel }}
          </v-btn>
        </div>
        <div
          v-if="canClone || canConvertProforma || canCreditNote"
          class="stock-invoice-hero__action-row"
        >
          <v-btn v-if="canClone" outlined color="white" small @click="cloneInvoice">
            <v-icon left small>mdi-content-copy</v-icon>
            {{ $t("STOCK.INVOICES.CLONE") }}
          </v-btn>
          <v-btn v-if="canConvertProforma" outlined color="white" small @click="convertProformaToInvoice">
            <v-icon left small>mdi-file-document-arrow-right-outline</v-icon>
            {{ $t("STOCK.INVOICES.CREATE_INVOICE_FROM_PROFORMA") }}
          </v-btn>
          <v-btn v-if="canCreditNote" outlined color="white" small @click="createCreditNote">
            {{ $t("STOCK.INVOICES.CREDIT_NOTE") }}
          </v-btn>
        </div>
        <div
          v-if="canMergeLines || canDownloadPdf || canRegeneratePdf"
          class="stock-invoice-hero__action-row"
        >
          <v-btn v-if="canMergeLines" outlined color="white" small @click="mergeLines">
            {{ $t("STOCK.INVOICES.MERGE_LINES") }}
          </v-btn>
          <v-btn v-if="canDownloadPdf" outlined color="white" small @click="downloadInvoicePdf">
            <v-icon left small>mdi-download</v-icon>
            {{ $t("GENERAL.LABELS.DOWNLOAD") }}
          </v-btn>
          <v-btn v-if="canRegeneratePdf" outlined color="white" small @click="regeneratePdf">
            <v-icon left small>mdi-refresh</v-icon>
            {{ $t("STOCK.DOCUMENTS.REGENERATE") }}
          </v-btn>
        </div>
        <div v-if="canSendInvoiceEmail" class="stock-invoice-hero__action-row">
          <v-btn outlined color="white" small :disabled="isProcessing" @click="openEmailDialog('invoice')">
            <v-icon left small>mdi-email-outline</v-icon>
            {{ $t("STOCK.INVOICES.SEND_INVOICE") }}
          </v-btn>
          <v-btn
            small
            :outlined="!document.reminder_due"
            :color="document.reminder_due ? '#ffe082' : 'white'"
            :class="document.reminder_due ? 'amber--text text--darken-4' : ''"
            :disabled="isProcessing"
            @click="openEmailDialog('reminder')"
          >
            <v-icon left small>mdi-bell-ring</v-icon>
            {{ $t("STOCK.INVOICES.SEND_REMINDER") }}
          </v-btn>
        </div>
        <div v-if="canCancel" class="stock-invoice-hero__action-row">
          <v-btn color="#ef9a9a" class="red--text text--darken-4" small @click="cancelInvoice">
            {{ $t("GENERAL.LABELS.CANCEL") }}
          </v-btn>
        </div>
      </template>
    </StockInvoiceHero>

    <v-row>
      <v-col cols="12" lg="9">
        <v-card :loading="isProcessing" :disabled="isProcessing">
          <v-tabs v-model="tab" class="stock-color" left dark>
            <v-tab href="#header">{{ $t("STOCK.INVOICES.TAB_HEADER") }}</v-tab>
            <v-tab href="#lines">{{ $t("STOCK.INVOICES.TAB_LINES") }}</v-tab>
            <v-tab href="#sources">{{ $t("STOCK.INVOICES.TAB_SOURCES") }}</v-tab>
            <v-tab href="#files">{{ $t("STOCK.INVOICES.TAB_FILES") }}</v-tab>
          </v-tabs>

          <v-tabs-items v-model="tab">
            <v-tab-item value="header">
              <v-card flat>
                <v-card-text>
                  <v-alert v-if="isReadOnly" type="warning" dense outlined class="mb-4">
                    {{ $t("STOCK.INVOICES.READ_ONLY") }}
                  </v-alert>
                  <section v-if="isNew" class="stock-invoice-section">
                    <div class="stock-invoice-section__head">
                      <v-icon small color="teal darken-1">mdi-swap-horizontal</v-icon>
                      <div>
                        <div class="stock-invoice-section__title">{{ $t("STOCK.INVOICES.DIRECTION") }}</div>
                      </div>
                    </div>
                    <v-item-group v-model="document.direction" mandatory class="stock-invoice-direction-picker">
                      <v-row dense>
                        <v-col cols="12" sm="6">
                          <v-item v-slot="{ active, toggle }" value="outgoing">
                            <v-card
                              :class="['stock-invoice-direction-card', { 'stock-invoice-direction-card--active': active }]"
                              outlined
                              @click="toggle"
                            >
                              <v-card-text class="d-flex align-center py-3">
                                <v-avatar color="teal darken-1" size="40" class="mr-3">
                                  <v-icon color="white" small>mdi-arrow-right-bold</v-icon>
                                </v-avatar>
                                <div>
                                  <div class="font-weight-medium">{{ $t("STOCK.INVOICES.DIRECTIONS.OUTGOING") }}</div>
                                  <div class="caption grey--text">{{ $t("STOCK.INVOICES.OUTGOING_HINT") }}</div>
                                </div>
                              </v-card-text>
                            </v-card>
                          </v-item>
                        </v-col>
                        <v-col cols="12" sm="6">
                          <v-item v-slot="{ active, toggle }" value="incoming">
                            <v-card
                              :class="['stock-invoice-direction-card', { 'stock-invoice-direction-card--active': active }]"
                              outlined
                              @click="toggle"
                            >
                              <v-card-text class="d-flex align-center py-3">
                                <v-avatar color="deep-purple darken-1" size="40" class="mr-3">
                                  <v-icon color="white" small>mdi-arrow-left-bold</v-icon>
                                </v-avatar>
                                <div>
                                  <div class="font-weight-medium">{{ $t("STOCK.INVOICES.DIRECTIONS.INCOMING") }}</div>
                                  <div class="caption grey--text">{{ $t("STOCK.INVOICES.INCOMING_HINT") }}</div>
                                </div>
                              </v-card-text>
                            </v-card>
                          </v-item>
                        </v-col>
                      </v-row>
                    </v-item-group>
                  </section>

                  <section class="stock-invoice-section">
                    <div class="stock-invoice-section__head">
                      <v-icon small color="teal darken-1">mdi-domain</v-icon>
                      <div>
                        <div class="stock-invoice-section__title">{{ $t("STOCK.INVOICES.SECTION_PARTIES") }}</div>
                        <div class="stock-invoice-section__hint">{{ $t("STOCK.INVOICES.SECTION_PARTIES_HINT") }}</div>
                      </div>
                    </div>
                    <v-row dense>
                      <v-col cols="12" md="4">
                        <v-autocomplete
                          v-model="document.organization_id"
                          :items="organizations"
                          item-text="label"
                          item-value="id"
                          :label="$t('STOCK.WAREHOUSES.ORGANIZATION') + '*'"
                          :disabled="headerDisabled"
                          :rules="headerDisabled ? [] : [validations.required()]"
                          :error-messages="validationErrors.organization_id || []"
                          @change="onOrganizationChange"
                        ></v-autocomplete>
                      </v-col>
                      <v-col cols="12" md="4">
                        <v-autocomplete
                          v-model="document.company_id"
                          :items="companyOptions"
                          :search-input.sync="companySearch"
                          item-text="label"
                          item-value="id"
                          :label="$t('STOCK.INVOICES.COMPANY') + '*'"
                          :disabled="headerDisabled"
                          clearable
                        ></v-autocomplete>
                      </v-col>
                      <v-col cols="12" md="4">
                        <v-autocomplete
                          v-model="document.deal_id"
                          :items="dealOptions"
                          item-text="label"
                          item-value="id"
                          :label="$t('STOCK.PURCHASE_ORDERS.DEAL')"
                          :disabled="headerDisabled"
                          clearable
                          @change="onDealChange"
                        ></v-autocomplete>
                        <StockDealReference
                          :deal-id="document.deal_id"
                          :deal="document.deal || selectedDealOption"
                        />
                      </v-col>
                      <v-col v-if="isIncoming" cols="12" md="6">
                        <v-text-field
                          v-model="document.external_number"
                          :label="$t('STOCK.INVOICES.SUPPLIER_INVOICE_NUMBER')"
                          :disabled="headerDisabled"
                          :error-messages="validationErrors.external_number || []"
                          required
                          @input="clearHeaderValidationError('external_number')"
                        ></v-text-field>
                      </v-col>
                    </v-row>
                  </section>

                  <section class="stock-invoice-section">
                    <div class="stock-invoice-section__head">
                      <v-icon small color="teal darken-1">mdi-calendar-range</v-icon>
                      <div>
                        <div class="stock-invoice-section__title">{{ $t("STOCK.INVOICES.SECTION_DATES") }}</div>
                        <div class="stock-invoice-section__hint">{{ $t("STOCK.INVOICES.SECTION_DATES_HINT") }}</div>
                      </div>
                    </div>
                    <v-row dense>
                      <v-col cols="12" sm="6" md="3">
                        <DatePicker
                          v-model="document.issue_date"
                          :label="$t('STOCK.INVOICES.ISSUE_DATE')"
                          :disabled="headerDisabled"
                          without-hint
                          clearable
                        ></DatePicker>
                      </v-col>
                      <v-col cols="12" sm="6" md="3">
                        <DatePicker
                          v-model="document.tax_event_date"
                          :label="$t('STOCK.INVOICES.TAX_EVENT_DATE')"
                          :disabled="headerDisabled"
                          without-hint
                          clearable
                        ></DatePicker>
                      </v-col>
                      <v-col cols="12" sm="6" md="3">
                        <v-text-field
                          v-model.number="dueDays"
                          type="number"
                          :label="$t('STOCK.INVOICES.DUE_DAYS')"
                          :disabled="headerDisabled"
                          @input="applyDueDays"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="12" sm="6" md="3">
                        <DatePicker
                          v-model="document.due_date"
                          :label="$t('STOCK.INVOICES.DUE_DATE')"
                          :disabled="headerDisabled"
                          without-hint
                          clearable
                        ></DatePicker>
                      </v-col>
                    </v-row>
                  </section>

                  <section class="stock-invoice-section">
                    <div class="stock-invoice-section__head">
                      <v-icon small color="teal darken-1">mdi-percent</v-icon>
                      <div>
                        <div class="stock-invoice-section__title">{{ $t("STOCK.INVOICES.SECTION_TAX") }}</div>
                        <div class="stock-invoice-section__hint">{{ $t("STOCK.INVOICES.SECTION_TAX_HINT") }}</div>
                      </div>
                    </div>
                    <v-row dense>
                      <v-col cols="12" sm="6" md="4">
                        <v-text-field
                          v-model="document.currency_code"
                          :label="$t('STOCK.PURCHASE_ORDERS.CURRENCY')"
                          :disabled="headerDisabled"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="12" sm="6" md="4">
                        <v-text-field
                          v-model.number="document.default_vat_rate"
                          type="number"
                          suffix="%"
                          :label="$t('STOCK.PURCHASE_ORDERS.DEFAULT_VAT')"
                          :disabled="headerDisabled"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="12" sm="6" md="4">
                        <v-checkbox
                          v-model="document.is_reverse_charge"
                          class="stock-invoice-reverse-charge mt-1"
                          color="primary"
                          hide-details
                          :label="$t('STOCK.INVOICES.REVERSE_CHARGE')"
                          :disabled="headerDisabled"
                        ></v-checkbox>
                        <div
                          class="stock-invoice-reverse-charge__hint"
                          :class="{ 'stock-invoice-reverse-charge__hint--warn': reverseChargeVatMismatch }"
                        >
                          {{ reverseChargeHint }}
                        </div>
                      </v-col>
                      <v-col cols="12">
                        <v-textarea
                          v-model="document.vat_transaction_basis"
                          :label="$t('STOCK.INVOICES.VAT_BASIS')"
                          rows="2"
                          auto-grow
                          :disabled="headerDisabled"
                        ></v-textarea>
                      </v-col>
                    </v-row>
                  </section>

                  <section class="stock-invoice-section">
                    <div class="stock-invoice-section__head">
                      <v-icon small color="teal darken-1">mdi-bank-outline</v-icon>
                      <div>
                        <div class="stock-invoice-section__title">{{ $t("STOCK.INVOICES.SECTION_PAYMENT") }}</div>
                        <div class="stock-invoice-section__hint">{{ $t("STOCK.INVOICES.SECTION_PAYMENT_HINT") }}</div>
                      </div>
                    </div>
                    <v-row dense>
                      <v-col cols="12" md="6">
                        <v-select
                          v-model="document.payment_method"
                          :items="paymentMethodOptions"
                          item-text="label"
                          item-value="value"
                          :label="$t('STOCK.INVOICES.PAYMENT_METHOD')"
                          :disabled="headerDisabled"
                        ></v-select>
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-autocomplete
                          v-model="document.organization_bank_account_id"
                          :items="bankAccounts"
                          item-text="label"
                          item-value="id"
                          :label="$t('STOCK.INVOICES.BANK_ACCOUNT')"
                          :disabled="headerDisabled"
                          clearable
                        ></v-autocomplete>
                      </v-col>
                      <v-col cols="12">
                        <v-combobox
                          v-model="document.payment_terms"
                          :items="paymentTermSnippets"
                          :label="$t('STOCK.INVOICES.PAYMENT_TERMS')"
                          :disabled="headerDisabled"
                          clearable
                        ></v-combobox>
                      </v-col>
                    </v-row>
                  </section>

                  <section class="stock-invoice-section">
                    <div class="stock-invoice-section__head">
                      <v-icon small color="teal darken-1">mdi-truck-outline</v-icon>
                      <div>
                        <div class="stock-invoice-section__title">{{ $t("STOCK.INVOICES.SECTION_DELIVERY") }}</div>
                        <div class="stock-invoice-section__hint">{{ $t("STOCK.INVOICES.SECTION_DELIVERY_HINT") }}</div>
                      </div>
                    </div>
                    <v-row dense>
                      <v-col cols="12" class="py-0">
                        <v-checkbox
                          v-model="document.includes_transport"
                          class="mt-0"
                          color="primary"
                          hide-details
                          :label="$t('STOCK.INVOICES.INCLUDES_TRANSPORT')"
                          :disabled="headerDisabled"
                        ></v-checkbox>
                      </v-col>
                      <v-col cols="12" md="4">
                        <v-autocomplete
                          v-model="document.incoterm_id"
                          :items="incotermOptions"
                          item-text="label"
                          item-value="id"
                          :label="$t('GENERAL.LABELS.INCOTERMS')"
                          :disabled="headerDisabled"
                          clearable
                          @change="onIncotermChange"
                        ></v-autocomplete>
                      </v-col>
                      <v-col cols="12" md="4">
                        <v-text-field
                          v-model="document.incoterm_named_place"
                          :label="$t('GENERAL.LABELS.INCOTERM_NAMED_PLACE')"
                          :disabled="headerDisabled"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="12" md="4">
                        <v-text-field
                          v-model="document.truck_container"
                          :label="$t('STOCK.INVOICES.TRUCK_CONTAINER')"
                          :disabled="headerDisabled"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-text-field
                          v-model="document.place_of_deal"
                          :label="$t('STOCK.INVOICES.PLACE_OF_DEAL')"
                          :disabled="headerDisabled"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-text-field
                          v-model="document.delivery_week"
                          :label="$t('STOCK.INVOICES.DELIVERY_WEEK')"
                          :disabled="headerDisabled"
                        ></v-text-field>
                      </v-col>
                    </v-row>
                  </section>

                  <section class="stock-invoice-section">
                    <div class="stock-invoice-section__head">
                      <v-icon small color="teal darken-1">mdi-text-box-outline</v-icon>
                      <div>
                        <div class="stock-invoice-section__title">{{ $t("STOCK.INVOICES.SECTION_DOCUMENT") }}</div>
                        <div class="stock-invoice-section__hint">{{ $t("STOCK.INVOICES.SECTION_DOCUMENT_HINT") }}</div>
                      </div>
                    </div>
                    <v-row dense>
                      <v-col v-if="canChooseEnglish" cols="12" sm="6" class="py-0">
                        <v-checkbox
                          class="mt-0"
                          color="primary"
                          hide-details
                          :label="$t('STOCK.INVOICES.ENGLISH_DOCUMENT')"
                          :disabled="headerDisabled"
                          :input-value="document.document_locale === 'en'"
                          @change="document.document_locale = $event ? 'en' : 'bg'"
                        ></v-checkbox>
                      </v-col>
                      <v-col cols="12" sm="6" class="py-0">
                        <v-checkbox
                          v-model="document.include_copy"
                          class="mt-0"
                          color="primary"
                          hide-details
                          :label="$t('STOCK.INVOICES.INCLUDE_COPY')"
                          :disabled="headerDisabled"
                        ></v-checkbox>
                      </v-col>
                      <v-col v-if="isDutchOrganization" cols="12" class="py-0">
                        <v-checkbox
                          v-model="document.show_gfsi_supply_chain_note"
                          class="mt-0"
                          color="primary"
                          hide-details
                          :label="$t('STOCK.INVOICES.GFSI_SUPPLY_CHAIN_NOTE')"
                          :disabled="headerDisabled"
                        ></v-checkbox>
                        <div class="stock-invoice-gfsi-note__hint">
                          {{ $t("STOCK.INVOICES.GFSI_SUPPLY_CHAIN_NOTE_TEXT") }}
                        </div>
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-textarea
                          v-model="document.notes"
                          :label="$t('STOCK.INVOICES.NOTES')"
                          rows="2"
                          auto-grow
                          :disabled="headerDisabled"
                        ></v-textarea>
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-text-field
                          v-model="document.unlabeled_text"
                          :label="$t('STOCK.INVOICES.UNLABELED_TEXT')"
                          :disabled="headerDisabled"
                        ></v-text-field>
                        <v-text-field
                          :value="preparedByName"
                          :label="$t('STOCK.INVOICES.AUTHOR_NAME')"
                          disabled
                        ></v-text-field>
                      </v-col>
                    </v-row>
                  </section>

                  <section v-if="!isIncoming" class="stock-invoice-section">
                    <div class="stock-invoice-section__head">
                      <v-icon small color="teal darken-1">mdi-card-account-details-outline</v-icon>
                      <div>
                        <div class="stock-invoice-section__title">{{ $t("STOCK.INVOICES.SECTION_PRINTED_PARTIES") }}</div>
                        <div class="stock-invoice-section__hint">{{ $t("STOCK.INVOICES.SECTION_PRINTED_PARTIES_HINT") }}</div>
                      </div>
                    </div>
                    <v-row>
                      <v-col cols="12" md="6" class="stock-invoice-party-col">
                        <div class="subtitle-2 mb-3">{{ $t("STOCK.INVOICES.BUYER_DETAILS") }}</div>
                        <v-text-field v-model="document.buyer_snapshot.name" :label="$t('STOCK.INVOICES.COMPANY_NAME')" :disabled="headerDisabled"></v-text-field>
                        <v-text-field v-model="document.buyer_snapshot.registration_number" :label="$t('STOCK.INVOICES.REGISTRATION_NO')" :disabled="headerDisabled"></v-text-field>
                        <v-text-field v-model="document.buyer_snapshot.vat_number" :label="$t('STOCK.INVOICES.VAT_NO')" :disabled="headerDisabled"></v-text-field>
                        <v-text-field v-model="document.buyer_snapshot.street" :label="$t('STOCK.INVOICES.STREET')" :disabled="headerDisabled"></v-text-field>
                        <v-text-field v-model="document.buyer_snapshot.city" :label="$t('STOCK.INVOICES.CITY')" :disabled="headerDisabled"></v-text-field>
                        <v-text-field v-model="document.buyer_snapshot.postal_code" :label="$t('STOCK.INVOICES.POSTAL_CODE')" :disabled="headerDisabled"></v-text-field>
                        <v-text-field v-model="document.buyer_snapshot.country" :label="$t('STOCK.INVOICES.COUNTRY')" :disabled="headerDisabled"></v-text-field>
                        <v-text-field v-model="document.buyer_snapshot.mol" :label="$t('STOCK.INVOICES.MOL')" :disabled="headerDisabled"></v-text-field>
                      </v-col>
                      <v-col cols="12" md="6" class="stock-invoice-party-col">
                        <div class="subtitle-2 mb-3">{{ $t("STOCK.INVOICES.SELLER_DETAILS") }}</div>
                        <v-text-field v-model="document.seller_snapshot.name" :label="$t('STOCK.INVOICES.COMPANY_NAME')" :disabled="headerDisabled"></v-text-field>
                        <v-text-field v-model="document.seller_snapshot.registration_number" :label="$t('STOCK.INVOICES.REGISTRATION_NO')" :disabled="headerDisabled"></v-text-field>
                        <v-text-field v-model="document.seller_snapshot.vat_number" :label="$t('STOCK.INVOICES.VAT_NO')" :disabled="headerDisabled"></v-text-field>
                        <v-text-field v-model="document.seller_snapshot.street" :label="$t('STOCK.INVOICES.STREET')" :disabled="headerDisabled"></v-text-field>
                        <v-text-field v-model="document.seller_snapshot.city" :label="$t('STOCK.INVOICES.CITY')" :disabled="headerDisabled"></v-text-field>
                        <v-text-field v-model="document.seller_snapshot.postal_code" :label="$t('STOCK.INVOICES.POSTAL_CODE')" :disabled="headerDisabled"></v-text-field>
                        <v-text-field v-model="document.seller_snapshot.country" :label="$t('STOCK.INVOICES.COUNTRY')" :disabled="headerDisabled"></v-text-field>
                        <v-text-field v-model="document.seller_snapshot.mol" :label="$t('STOCK.INVOICES.MOL')" :disabled="headerDisabled"></v-text-field>
                        <v-text-field v-model="document.seller_snapshot.eori_number" label="EORI" :disabled="headerDisabled"></v-text-field>
                      </v-col>
                    </v-row>
                  </section>

                  <StockInvoicePaymentsPanel
                    v-if="canViewPayments"
                    ref="paymentsPanel"
                    class="mt-2"
                    :payments="document.payments || []"
                    :paid-amount="document.paid_amount"
                    :remaining-amount="document.remaining_amount"
                    :currency-code="document.currency_code"
                    :can-record="canRecordPayment"
                    :settlement="document.type === 'credit_note' ? 'refund' : 'payment'"
                    :direction="document.direction"
                    @record="recordPayment"
                    @cancel="cancelPayment"
                  />

                  <StockInvoiceEmailPanel
                    v-if="canViewEmailHistory"
                    class="mt-4"
                    :sends="document.email_sends || []"
                    :reminder-due="!!document.reminder_due"
                    @download="downloadEmailSnapshot"
                  />

                  <StockInvoiceReferencesPanel
                    v-if="!isNew"
                    class="mt-4"
                    :source-invoice="document.source_invoice"
                    :derived-invoices="document.derived_invoices || []"
                  />
                </v-card-text>
              </v-card>
            </v-tab-item>

            <v-tab-item value="lines">
              <v-card flat>
                <v-card-text>
                  <StockInvoiceLinesEditor
                    v-model="lines"
                    :disabled="!canEditLines"
                    :currency-code="document.currency_code"
                    :default-vat-rate="document.default_vat_rate"
                    :document-locale="document.document_locale || 'bg'"
                  />
                </v-card-text>
              </v-card>
            </v-tab-item>

            <v-tab-item value="sources">
              <v-card flat>
                <v-card-text>
                  <StockInvoiceSourcesPanel
                    :actions="document.stock_actions || []"
                    :delivery-notes="document.delivery_notes || []"
                    :removable="canSave"
                    :removing-action-id="removingSourceActionId"
                    :removing-delivery-note-id="removingDeliveryNoteId"
                    @remove="removeSourceAction"
                    @remove-delivery-note="removeDeliveryNote"
                  />
                  <v-autocomplete
                    v-if="canSave"
                    v-model="addDeliveryNoteId"
                    class="mt-4"
                    :items="deliveryNoteOptions"
                    :loading="loadingDeliveryNotes"
                    :search-input.sync="deliveryNoteSearch"
                    item-text="label"
                    item-value="id"
                    :label="$t('STOCK.INVOICES.ADD_DELIVERY_NOTE')"
                    clearable
                    outlined
                    dense
                    hide-details="auto"
                    @change="addDeliveryNote"
                  />
                </v-card-text>
              </v-card>
            </v-tab-item>

            <v-tab-item value="files">
              <v-card flat>
                <v-card-text>
                  <StockDocumentFilesPanel
                    document-kind="invoice"
                    :document-id="document.id"
                    :files="documentFiles"
                    :can-regenerate="canRegeneratePdf"
                    @files-updated="onFilesUpdated"
                  />
                </v-card-text>
              </v-card>
            </v-tab-item>
          </v-tabs-items>
        </v-card>
      </v-col>

      <v-col cols="12" lg="3">
        <div class="stock-invoice-totals-sticky">
          <StockInvoiceTotalsPanel
            :net="displayTotals.total_net"
            :vat="displayTotals.total_vat"
            :gross="displayTotals.total_gross"
            :currency-code="document.currency_code"
            :direction="document.direction"
          />
        </div>
      </v-col>
    </v-row>
    </template>

    <v-dialog v-model="showEmailDialog" max-width="560" persistent>
      <v-card>
        <v-card-title>{{ emailDialogTitle }}</v-card-title>
        <v-card-text>
          <v-alert type="info" dense outlined class="mb-4">
            {{ $t("STOCK.INVOICES.EMAIL_REGENERATE_WARNING") }}
          </v-alert>
          <div class="caption grey--text text--darken-1 mb-1">{{ $t("STOCK.INVOICES.EMAIL_RECIPIENTS") }}</div>
          <div v-if="invoiceEmailRecipients.length" class="mb-4">
            <v-chip
              v-for="recipient in invoiceEmailRecipients"
              :key="recipient.company_general_email_id || recipient.email"
              small
              class="mr-2 mb-2"
            >
              {{ recipient.email }}
            </v-chip>
          </div>
          <v-alert v-else type="error" dense outlined class="mb-4">
            {{ $t("STOCK.INVOICES.EMAIL_NO_RECIPIENTS") }}
          </v-alert>
          <v-textarea
            v-model="emailForm.message_note"
            :label="$t('STOCK.INVOICES.EMAIL_NOTE')"
            :hint="$t('STOCK.INVOICES.EMAIL_NOTE_HINT')"
            persistent-hint
            outlined
            rows="4"
            auto-grow
            counter="5000"
            maxlength="5000"
          ></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text :disabled="isProcessing" @click="closeEmailDialog">
            {{ $t("GENERAL.LABELS.CANCEL") }}
          </v-btn>
          <v-btn
            color="teal"
            dark
            :loading="isProcessing"
            :disabled="!canConfirmEmailSend"
            @click="confirmEmailSend"
          >
            {{ $t("STOCK.INVOICES.EMAIL_CONFIRM") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="showIssueUnsavedDialog" max-width="480" persistent>
      <v-card>
        <v-card-title>{{ $t("STOCK.INVOICES.ISSUE_UNSAVED_TITLE") }}</v-card-title>
        <v-card-text>
          <p class="body-2 grey--text text--darken-1 mb-0">
            {{ $t("STOCK.INVOICES.ISSUE_UNSAVED_HINT") }}
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text :disabled="isProcessing" @click="closeIssueUnsavedDialog">
            {{ $t("GENERAL.LABELS.CANCEL") }}
          </v-btn>
          <v-btn
            color="success"
            :loading="isProcessing"
            @click="confirmSaveAndIssue"
          >
            {{ $t("STOCK.INVOICES.SAVE_AND_ISSUE") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="showCloneDialog" max-width="480" persistent>
      <v-card>
        <v-card-title>{{ $t("STOCK.INVOICES.CLONE_INVOICE") }}</v-card-title>
        <v-card-text>
          <p class="body-2 grey--text text--darken-1 mb-4">
            {{ $t("STOCK.INVOICES.CLONE_EXTERNAL_HINT") }}
          </p>
          <v-text-field
            v-model="cloneForm.external_number"
            :label="$t('STOCK.INVOICES.SUPPLIER_INVOICE_NUMBER') + '*'"
            :error-messages="cloneValidationErrors.external_number || []"
            autofocus
            @keyup.enter="confirmCloneInvoice"
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text :disabled="isProcessing" @click="closeCloneDialog">
            {{ $t("GENERAL.LABELS.CANCEL") }}
          </v-btn>
          <v-btn
            color="primary"
            :loading="isProcessing"
            :disabled="!canSubmitClone"
            @click="confirmCloneInvoice"
          >
            {{ $t("STOCK.INVOICES.CLONE") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="showCreditNoteDialog" max-width="480" persistent>
      <v-card>
        <v-card-title>{{ $t("STOCK.INVOICES.CREATE_CREDIT_NOTE") }}</v-card-title>
        <v-card-text>
          <p class="body-2 grey--text text--darken-1 mb-4">
            {{ $t("STOCK.INVOICES.CREDIT_NOTE_EXTERNAL_HINT") }}
          </p>
          <v-text-field
            v-model="creditNoteForm.external_number"
            :label="$t('STOCK.INVOICES.SUPPLIER_INVOICE_NUMBER') + '*'"
            :error-messages="creditNoteValidationErrors.external_number || []"
            autofocus
            @keyup.enter="confirmCreateCreditNote"
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text :disabled="isProcessing" @click="closeCreditNoteDialog">
            {{ $t("GENERAL.LABELS.CANCEL") }}
          </v-btn>
          <v-btn
            color="info"
            :loading="isProcessing"
            :disabled="!canSubmitCreditNote"
            @click="confirmCreateCreditNote"
          >
            {{ $t("GENERAL.LABELS.CREATE_BUTTON") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { debounce } from "lodash";
import DatePicker from "@/views/partials/form/DatePicker.vue";
import StockDocumentPageHeading from "@/views/partials/stock/StockDocumentPageHeading.vue";
import StockDocumentFilesPanel from "@/views/partials/stock/StockDocumentFilesPanel.vue";
import StockDealReference from "@/views/partials/stock/StockDealReference.vue";
import StockInvoiceHero from "@/views/partials/stock/invoices/StockInvoiceHero.vue";
import StockInvoiceTotalsPanel from "@/views/partials/stock/invoices/StockInvoiceTotalsPanel.vue";
import StockInvoiceLinesEditor from "@/views/partials/stock/invoices/StockInvoiceLinesEditor.vue";
import StockInvoiceSourcesPanel from "@/views/partials/stock/invoices/StockInvoiceSourcesPanel.vue";
import StockInvoiceReferencesPanel from "@/views/partials/stock/invoices/StockInvoiceReferencesPanel.vue";
import StockInvoicePaymentsPanel from "@/views/partials/stock/invoices/StockInvoicePaymentsPanel.vue";
import StockInvoiceEmailPanel from "@/views/partials/stock/invoices/StockInvoiceEmailPanel.vue";
import stockDocumentContextMixin from "@/views/partials/stock/stockDocumentContext.mixin.js";
import stockDocumentOptionsMixin from "@/views/partials/stock/stockDocumentOptions.mixin.js";
import stockDocumentTotalsMixin from "@/views/partials/stock/stockDocumentTotals.mixin.js";
import { invoiceProductDescription } from "@/common/stock/invoiceProductDescription";
import { mapGetters } from "vuex";
import { SHOW_SNACK } from "@/store/snack.module";
import { GET_COMPANIES_OPTIONS } from "@/store/companies.module";
import { GET_STOCK_DEAL_OPTIONS } from "@/store/stock/stockReceipts.module";
import { GET_INCOTERM_OPTIONS } from "@/store/incoterms.module";
import {
  GET_STOCK_INVOICE,
  CREATE_STOCK_INVOICE,
  UPDATE_STOCK_INVOICE,
  ISSUE_STOCK_INVOICE,
  CANCEL_STOCK_INVOICE,
  MERGE_STOCK_INVOICE_LINES,
  REMOVE_STOCK_INVOICE_ACTIONS,
  CREATE_STOCK_CREDIT_NOTE,
  CREATE_INVOICE_FROM_PROFORMA,
  CLONE_STOCK_INVOICE,
  REGENERATE_STOCK_INVOICE_DOCUMENT,
  DOWNLOAD_STOCK_INVOICE_FILE,
  GET_ORGANIZATION_BANK_ACCOUNTS,
  GET_INVOICE_PAYMENT_TERM_SNIPPETS,
  RECORD_STOCK_INVOICE_PAYMENT,
  CANCEL_STOCK_INVOICE_PAYMENT,
  RECORD_STOCK_INVOICE_EMAIL_SEND,
  DOWNLOAD_STOCK_INVOICE_EMAIL_SEND,
} from "@/store/stock/stockInvoices.module";
import {
  GET_STOCK_DELIVERY_NOTES,
  ADD_STOCK_INVOICE_DELIVERY_NOTES,
  REMOVE_STOCK_INVOICE_DELIVERY_NOTES,
} from "@/store/stock/stockDeliveryNotes.module";

export default {
  name: "StockViewInvoice",
  components: {
    DatePicker,
    StockDocumentPageHeading,
    StockDocumentFilesPanel,
    StockDealReference,
    StockInvoiceHero,
    StockInvoiceTotalsPanel,
    StockInvoiceLinesEditor,
    StockInvoiceSourcesPanel,
    StockInvoiceReferencesPanel,
    StockInvoicePaymentsPanel,
    StockInvoiceEmailPanel,
  },
  mixins: [stockDocumentContextMixin, stockDocumentOptionsMixin, stockDocumentTotalsMixin],
  data() {
    return {
      tab: "header",
      isProcessing: false,
      isDocumentReady: false,
      documentLoadFailed: false,
      validationErrors: {},
      companySearch: "",
      companyOptions: [],
      organizations: [],
      dealOptions: [],
      document: this.emptyDocument(),
      lines: [],
      documentFiles: [],
      removingSourceActionId: null,
      removingDeliveryNoteId: null,
      addDeliveryNoteId: null,
      deliveryNoteOptions: [],
      deliveryNoteSearch: "",
      loadingDeliveryNotes: false,
      lineQuantityKey: "quantity",
      showCreditNoteDialog: false,
      creditNoteForm: {
        external_number: "",
      },
      creditNoteValidationErrors: {},
      showCloneDialog: false,
      cloneForm: {
        external_number: "",
      },
      cloneValidationErrors: {},
      showIssueUnsavedDialog: false,
      showEmailDialog: false,
      emailForm: {
        kind: "invoice",
        message_note: "",
      },
      savedPayloadSignature: "",
      dueDays: null,
      bankAccounts: [],
      incotermOptions: [],
      paymentTermSnippets: [],
    };
  },
  computed: {
    ...mapGetters(["currentUser"]),
    preparedByName() {
      const stored = String(this.document.author_name || "").trim();
      if (stored) {
        return stored;
      }
      const user = this.currentUser || {};
      return String(user.invoice_name || user.name || "").trim();
    },
    isNew() {
      return this.$route.name === "stock-invoices-create";
    },
    companyName() {
      const company = this.document.company;
      if (company) {
        return company.label || company.name || "";
      }
      const match = (this.companyOptions || []).find(
        (c) => Number(c.id) === Number(this.document.company_id)
      );
      return match ? match.label || match.name : "";
    },
    isReadOnly() {
      return !this.isNew && !this.canEditLines && !this.canSave;
    },
    headerDisabled() {
      return !this.canSave;
    },
    canSave() {
      if (this.isNew) {
        return this.hasPermissions("stock-invoices-create");
      }
      return (
        this.document.status !== "cancelled" &&
        this.hasPermissions("stock-invoices-edit")
      );
    },
    canEditLines() {
      return this.canSave;
    },
    canIssue() {
      return (
        !this.isNew &&
        this.document.status === "draft" &&
        this.hasPermissions("stock-invoices-issue")
      );
    },
    isDirty() {
      if (this.isNew) {
        return true;
      }
      return this.documentSignature() !== this.savedPayloadSignature;
    },
    isProforma() {
      return this.document.type === "proforma";
    },
    issueActionLabel() {
      return this.isProforma
        ? this.$t("STOCK.INVOICES.ISSUE_PROFORMA")
        : this.$t("STOCK.INVOICES.ISSUE");
    },
    canMergeLines() {
      return !this.isNew && this.canSave;
    },
    canLock() {
      return false;
    },
    canCancel() {
      return (
        !this.isNew &&
        this.document.status === "draft" &&
        this.hasPermissions("stock-invoices-cancel")
      );
    },
    isIncoming() {
      return this.document.direction === "incoming";
    },
    displayDocumentCode() {
      if (this.isNew) {
        return "";
      }
      if (this.document.direction === "incoming") {
        return this.document.external_number || this.document.code || "";
      }
      return this.document.fiscal_number || this.document.code || "";
    },
    canConvertProforma() {
      return (
        !this.isNew &&
        this.document.type === "proforma" &&
        !["cancelled"].includes(this.document.status) &&
        this.hasPermissions("stock-invoices-create")
      );
    },
    canCreditNote() {
      return (
        !this.isNew &&
        this.document.type === "invoice" &&
        ["issued", "partially_paid", "paid"].includes(this.document.status) &&
        this.hasPermissions("stock-invoices-create")
      );
    },
    canClone() {
      return (
        !this.isNew &&
        this.document.status !== "cancelled" &&
        this.hasPermissions("stock-invoices-create")
      );
    },
    canSubmitClone() {
      return !!(this.cloneForm.external_number || "").trim();
    },
    hasHeroActions() {
      return (
        this.canSave ||
        this.canIssue ||
        this.canClone ||
        this.canConvertProforma ||
        this.canCreditNote ||
        this.canMergeLines ||
        this.canDownloadPdf ||
        this.canRegeneratePdf ||
        this.canSendInvoiceEmail ||
        this.canCancel
      );
    },
    canSubmitCreditNote() {
      return !!(this.creditNoteForm.external_number || "").trim();
    },
    generatedInvoicePdf() {
      return (this.documentFiles || []).find(
        (file) => file.source === "generated" || file.document_type
      );
    },
    canDownloadPdf() {
      return (
        !this.isNew &&
        this.document.direction === "outgoing" &&
        ["issued", "partially_paid", "paid"].includes(this.document.status) &&
        !!this.generatedInvoicePdf
      );
    },
    canRegeneratePdf() {
      return (
        !this.isNew &&
        this.document.direction === "outgoing" &&
        ["issued", "partially_paid", "paid"].includes(this.document.status) &&
        this.hasPermissions("stock-invoices-edit")
      );
    },
    canViewEmailHistory() {
      return (
        !this.isNew &&
        this.document.direction === "outgoing" &&
        ["issued", "partially_paid", "paid"].includes(this.document.status)
      );
    },
    canSendInvoiceEmail() {
      return this.canViewEmailHistory && this.hasPermissions("stock-invoices-send");
    },
    invoiceEmailRecipients() {
      return this.document.invoice_email_recipients || [];
    },
    canConfirmEmailSend() {
      return this.invoiceEmailRecipients.length > 0 && !this.isProcessing;
    },
    emailDialogTitle() {
      return this.emailForm.kind === "reminder"
        ? this.$t("STOCK.INVOICES.EMAIL_DIALOG_TITLE_REMINDER")
        : this.$t("STOCK.INVOICES.EMAIL_DIALOG_TITLE_INVOICE");
    },
    displayTotals() {
      if (this.canEditLines && this.lines.length) {
        return this.previewInvoiceTotals();
      }
      return {
        total_net: this.document.total_net,
        total_vat: this.document.total_vat,
        total_gross: this.document.total_gross,
      };
    },
    reverseChargeVatMismatch() {
      return (
        !!this.document.is_reverse_charge &&
        Math.abs(Number(this.displayTotals.total_vat || 0)) >= 0.0001
      );
    },
    reverseChargeHint() {
      if (this.reverseChargeVatMismatch) {
        return this.$t("STOCK.INVOICES.REVERSE_CHARGE_NEEDS_ZERO_VAT");
      }
      return this.$t("STOCK.INVOICES.REVERSE_CHARGE_HINT");
    },
    selectedOrganization() {
      return (this.organizations || []).find(
        (org) => Number(org.id) === Number(this.document.organization_id)
      ) || null;
    },
    isDutchOrganization() {
      return String(this.selectedOrganization?.code || "").toUpperCase() === "NL";
    },
    canChooseEnglish() {
      return !this.isDutchOrganization;
    },
    canViewPayments() {
      return (
        !this.isNew &&
        ["issued", "partially_paid", "paid"].includes(this.document.status)
      );
    },
    canRecordPayment() {
      return (
        !this.isNew &&
        ["issued", "partially_paid", "paid"].includes(this.document.status) &&
        this.hasPermissions("stock-invoices-edit")
      );
    },
    paymentMethodOptions() {
      return [
        { value: "bank_transfer", label: this.$t("STOCK.INVOICES.BANK_TRANSFER") },
        { value: "cash", label: this.$t("STOCK.INVOICES.CASH") },
      ];
    },
  },
  watch: {
    companySearch() {
      this.loadCompanies();
    },
    deliveryNoteSearch: debounce(function () {
      this.loadAddableDeliveryNotes();
    }, 300),
    tab(value) {
      if (value === "sources" && this.canSave) {
        this.loadAddableDeliveryNotes();
      }
    },
    "$route.params.id"() {
      if (this.isNew) {
        return;
      }
      this.isDocumentReady = false;
      this.documentLoadFailed = false;
      this.loadDocument();
    },
  },
  mounted() {
    this.bootstrapPage();
  },
  methods: {
    emptyDocument() {
      const today = new Date().toISOString().slice(0, 10);
      return {
        id: null,
        code: "",
        status: "draft",
        type: "invoice",
        direction: "outgoing",
        organization_id: null,
        company_id: null,
        deal_id: null,
        external_number: "",
        issue_date: today,
        tax_event_date: today,
        due_date: null,
        currency_code: "EUR",
        default_vat_rate: 0,
        is_reverse_charge: false,
        notes: "",
        is_locked: false,
        fiscal_number: "",
        document_locale: "bg",
        include_copy: true,
        show_gfsi_supply_chain_note: false,
        incoterm_id: null,
        incoterm_named_place: "",
        truck_container: "",
        place_of_deal: "",
        vat_transaction_basis: "",
        payment_method: "bank_transfer",
        organization_bank_account_id: null,
        seller_snapshot: this.emptyPartySnapshot(),
        buyer_snapshot: this.emptyPartySnapshot(),
        includes_transport: false,
        paid_amount: 0,
        remaining_amount: 0,
        payments: [],
        email_sends: [],
        invoice_email_recipients: [],
        reminder_due: false,
        delivery_week: "",
        unlabeled_text: "",
        payment_terms: "",
        author_name: "",
        total_net: 0,
        total_vat: 0,
        total_gross: 0,
        stock_actions: [],
        delivery_notes: [],
      };
    },
    async bootstrapPage() {
      await this.loadOrganizations();
      await this.loadDeals();
      await this.loadCompanies();
      await this.loadIncoterms();
      if (!this.isNew) {
        await this.loadDocument();
      } else {
        this.applyStockContextDefaults();
        const query = this.$route.query || {};
        if (query.direction) this.document.direction = query.direction;
        if (query.type) this.document.type = query.type;
        if (query.organization_id) this.document.organization_id = Number(query.organization_id);
        if (query.company_id) this.document.company_id = Number(query.company_id);
        if (query.deal_id) {
          this.applyDealIdFromQuery(Number(query.deal_id));
        }
        this.applyDealPartiesToDocument(this.resolveDealForParties());
        this.applyOrganizationDefaults();
        await this.loadBankAccounts();
        await this.loadPaymentTermSnippets();
        this.isDocumentReady = true;
      }
    },
    applyStockContextDefaults() {
      const orgIds = this.stockContextOrganizationIds;
      if (!this.document.organization_id && orgIds.length === 1) {
        this.document.organization_id = orgIds[0];
      }
    },
    async loadCompanies() {
      try {
        const data = await this.$store.dispatch(GET_COMPANIES_OPTIONS, {
          search: this.companySearch || undefined,
          limit: 50,
        });
        this.companyOptions = data.data || data || [];
        this.preserveDealPartyCompaniesInOptions();
      } catch {
        this.companyOptions = [];
        this.preserveDealPartyCompaniesInOptions();
      }
    },
    async loadDocument() {
      this.isDocumentReady = false;
      this.documentLoadFailed = false;
      this.isProcessing = true;
      try {
        const data = await this.$store.dispatch(GET_STOCK_INVOICE, this.$route.params.id);
        await this.applyDocument(data);
        this.isDocumentReady = true;
      } catch (errors) {
        this.documentLoadFailed = true;
        this.$store.dispatch(SHOW_SNACK, {
          message: (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      } finally {
        this.isProcessing = false;
      }
    },
    async applyDocument(data) {
      this.document = { ...this.emptyDocument(), ...data };
      this.document.is_reverse_charge = !!data.is_reverse_charge;
      this.document.include_copy = data.include_copy !== false;
      this.document.show_gfsi_supply_chain_note = !!data.show_gfsi_supply_chain_note;
      this.document.includes_transport = !!data.includes_transport;
      this.document.seller_snapshot = { ...this.emptyPartySnapshot(), ...(data.seller_snapshot || {}) };
      this.document.buyer_snapshot = { ...this.emptyPartySnapshot(), ...(data.buyer_snapshot || {}) };
      this.lines = (data.items || []).map((item) => this.mapInvoiceLineFromApi(item));
      this.documentFiles = Array.isArray(data.files) ? data.files : [];
      if (data.company) {
        this.companyOptions = this.mergeOptionIntoList(this.companyOptions, data.company);
      }
      if (data.organization) {
        this.organizations = this.mergeOptionIntoList(this.organizations, {
          id: data.organization.id,
          label: data.organization.name || data.organization.code,
          ...data.organization,
        });
      }
      this.syncDealOptionFromDraft(data);
      await this.loadBankAccounts();
      await this.loadPaymentTermSnippets();
      this.markClean();
    },
    async loadDeals() {
      const responseData = await this.$store.dispatch(GET_STOCK_DEAL_OPTIONS, {
        organization_id: this.document.organization_id,
        limit: 50,
      });
      this.applyDealOptionsResponse(responseData);
    },
    mapInvoiceLineFromApi(item) {
      const qty = Number(item.quantity || 0);
      const unitPrice = Number(item.unit_price_net || 0);
      return {
        id: item.id,
        line_no: item.line_no,
        description: item.description || "",
        stock_product_id: item.stock_product_id,
        stock_product: item.stock_product || null,
        quantity: qty,
        unit_id: item.unit_id,
        unit: item.unit || null,
        unit_price_net: unitPrice,
        line_total_net: item.line_total_net != null ? Number(item.line_total_net) : qty * unitPrice,
        vat_rate: item.vat_rate,
        hs_code: item.hs_code || (item.stock_product && item.stock_product.code) || "",
      };
    },
    previewInvoiceTotals() {
      let totalNet = 0;
      let totalVat = 0;
      const defaultVat = Number(this.document.default_vat_rate ?? 0);

      this.lines.forEach((line) => {
        const lineNet = Number(line.line_total_net || 0);
        const vatRate = Number(line.vat_rate ?? defaultVat);
        totalNet += lineNet;
        totalVat += this.roundMoney((lineNet * vatRate) / 100);
      });

      totalNet = this.roundMoney(totalNet);
      totalVat = this.roundMoney(totalVat);
      return {
        total_net: totalNet,
        total_vat: totalVat,
        total_gross: this.roundMoney(totalNet + totalVat),
      };
    },
    payload() {
      return {
        organization_id: this.document.organization_id,
        company_id: this.document.company_id,
        deal_id: this.document.deal_id,
        type: this.document.type,
        direction: this.document.direction,
        external_number: this.document.external_number,
        issue_date: this.document.issue_date,
        due_date: this.document.due_date,
        currency_code: this.document.currency_code,
        default_vat_rate: this.document.default_vat_rate,
        is_reverse_charge: !!this.document.is_reverse_charge,
        notes: this.document.notes,
        document_locale: this.isDutchOrganization ? "en" : this.document.document_locale,
        include_copy: !!this.document.include_copy,
        show_gfsi_supply_chain_note:
          this.isDutchOrganization && !!this.document.show_gfsi_supply_chain_note,
        tax_event_date: this.document.tax_event_date,
        incoterm_id: this.document.incoterm_id,
        incoterm_named_place: this.document.incoterm_named_place,
        truck_container: this.document.truck_container,
        place_of_deal: this.document.place_of_deal,
        vat_transaction_basis: this.document.vat_transaction_basis,
        payment_method: this.document.payment_method,
        organization_bank_account_id: this.document.organization_bank_account_id,
        includes_transport: !!this.document.includes_transport,
        delivery_week: this.document.delivery_week,
        unlabeled_text: this.document.unlabeled_text,
        payment_terms: this.document.payment_terms,
        author_name: this.preparedByName,
        seller_snapshot: this.document.seller_snapshot,
        buyer_snapshot: this.document.buyer_snapshot,
        items: this.lines.map((line, index) => ({
          id: line.id || undefined,
          line_no: index + 1,
          description: line.description || this.lineProductLabel(line) || "Line",
          hs_code: line.hs_code || undefined,
          stock_product_id: line.stock_product_id || undefined,
          quantity: line.quantity,
          unit_id: line.unit_id || undefined,
          unit_price_net: line.unit_price_net,
          vat_rate: line.vat_rate ?? this.document.default_vat_rate,
        })),
      };
    },
    lineProductLabel(line) {
      if (line.description) {
        return line.description;
      }
      return invoiceProductDescription(line.stock_product);
    },
    clearHeaderValidationError(field) {
      if (this.validationErrors[field]) {
        this.$delete(this.validationErrors, field);
      }
    },
    documentSignature() {
      return JSON.stringify(this.payload());
    },
    markClean() {
      this.savedPayloadSignature = this.documentSignature();
    },
    async save({ silent } = {}) {
      this.isProcessing = true;
      try {
        this.validationErrors = {};
        const params = this.payload();
        let data;
        if (this.isNew) {
          data = await this.$store.dispatch(CREATE_STOCK_INVOICE, params);
          this.$router.replace({ name: "stock-invoices-view", params: { id: data.id } });
        } else {
          data = await this.$store.dispatch(UPDATE_STOCK_INVOICE, {
            id: this.$route.params.id,
            params,
          });
        }
        await this.applyDocument(data);
        if (!silent) {
          this.$store.dispatch(SHOW_SNACK, {
            message: this.$t("GENERAL.SUCCESS_MESSAGE"),
            color: "success",
          });
        }
        return true;
      } catch (errors) {
        this.validationErrors = (errors && errors.errors) || {};
        this.$store.dispatch(SHOW_SNACK, {
          message: (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
        return false;
      } finally {
        this.isProcessing = false;
      }
    },
    async runAction(action, successMessage) {
      this.isProcessing = true;
      try {
        const data = await this.$store.dispatch(action, this.$route.params.id);
        await this.applyDocument(data);
        this.$store.dispatch(SHOW_SNACK, {
          message: successMessage || this.$t("GENERAL.SUCCESS_MESSAGE"),
          color: "success",
        });
      } catch (errors) {
        this.$store.dispatch(SHOW_SNACK, {
          message: (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      } finally {
        this.isProcessing = false;
      }
    },
    issueInvoice() {
      if (this.canSave && this.isDirty) {
        this.showIssueUnsavedDialog = true;
        return;
      }
      this.submitIssueInvoice();
    },
    closeIssueUnsavedDialog() {
      if (this.isProcessing) {
        return;
      }
      this.showIssueUnsavedDialog = false;
    },
    async confirmSaveAndIssue() {
      const saved = await this.save({ silent: true });
      if (!saved) {
        this.showIssueUnsavedDialog = false;
        return;
      }
      this.showIssueUnsavedDialog = false;
      await this.submitIssueInvoice();
    },
    submitIssueInvoice() {
      const successMessage = this.isProforma
        ? this.$t("STOCK.INVOICES.ISSUE_PROFORMA_SUCCESS")
        : this.$t("STOCK.INVOICES.ISSUE_SUCCESS");
      return this.runAction(ISSUE_STOCK_INVOICE, successMessage);
    },
    cancelInvoice() {
      if (!window.confirm(this.$t("STOCK.INVOICES.CANCEL_CONFIRM"))) {
        return;
      }
      this.runAction(CANCEL_STOCK_INVOICE);
    },
    lockInvoice() {
      return;
    },
    emptyPartySnapshot() {
      return {
        name: "",
        registration_number: "",
        vat_number: "",
        city: "",
        country: "",
        street: "",
        postal_code: "",
        mol: "",
        eori_number: "",
      };
    },
    applyDueDays() {
      const days = Number(this.dueDays);
      if (!Number.isFinite(days) || days <= 0) {
        return;
      }
      const date = new Date();
      date.setDate(date.getDate() + days);
      this.document.due_date = date.toISOString().slice(0, 10);
    },
    applyOrganizationDefaults() {
      const org = this.selectedOrganization;
      if (!org) {
        return;
      }
      const isDutch = String(org.code || "").toUpperCase() === "NL";
      this.document.document_locale = isDutch ? "en" : (this.document.document_locale || "bg");
      if (!isDutch) {
        this.document.show_gfsi_supply_chain_note = false;
      }
      if (!this.document.place_of_deal) {
        this.document.place_of_deal = org.place_of_deal || "";
      }
      if (!this.document.seller_snapshot || !this.document.seller_snapshot.name) {
        this.document.seller_snapshot = {
          ...this.emptyPartySnapshot(),
          name: org.legal_name || org.label || org.name || "",
          registration_number: org.registration_number || "",
          vat_number: org.vat_number || "",
          city: org.city || "",
          country: org.country || "",
          street: org.street || "",
          postal_code: org.postal_code || "",
          mol: org.mol || "",
          eori_number: org.eori_number || "",
        };
      }
    },
    async onOrganizationChange() {
      this.clearHeaderValidationError("organization_id");
      this.applyOrganizationDefaults();
      await this.loadBankAccounts();
      await this.loadPaymentTermSnippets();
    },
    onIncotermChange(id) {
      const option = (this.incotermOptions || []).find((item) => Number(item.id) === Number(id));
      if (option && option.include_transport != null) {
        this.document.includes_transport = !!option.include_transport;
      }
    },
    async loadIncoterms() {
      try {
        this.incotermOptions = await this.$store.dispatch(GET_INCOTERM_OPTIONS);
        if (this.incotermOptions && this.incotermOptions.data) {
          this.incotermOptions = this.incotermOptions.data;
        }
      } catch {
        this.incotermOptions = [];
      }
    },
    async loadBankAccounts() {
      if (!this.document.organization_id) {
        this.bankAccounts = [];
        return;
      }
      try {
        this.bankAccounts = await this.$store.dispatch(GET_ORGANIZATION_BANK_ACCOUNTS, {
          organization_id: this.document.organization_id,
        });
        if (!this.document.organization_bank_account_id) {
          const def = (this.bankAccounts || []).find((account) => account.is_default);
          if (def) {
            this.document.organization_bank_account_id = def.id;
          }
        }
      } catch {
        this.bankAccounts = [];
      }
    },
    async loadPaymentTermSnippets() {
      if (!this.document.organization_id) {
        this.paymentTermSnippets = [];
        return;
      }
      try {
        const data = await this.$store.dispatch(GET_INVOICE_PAYMENT_TERM_SNIPPETS, {
          organization_id: this.document.organization_id,
        });
        this.paymentTermSnippets = (data || []).map((item) => item.text || item);
      } catch {
        this.paymentTermSnippets = [];
      }
    },
    async recordPayment(payload) {
      const amount = Number(payload?.amount);
      if (!amount || amount <= 0) {
        return;
      }
      this.isProcessing = true;
      try {
        const data = await this.$store.dispatch(RECORD_STOCK_INVOICE_PAYMENT, {
          id: this.$route.params.id,
          params: {
            amount,
            paid_at: payload.paid_at || undefined,
          },
        });
        if (this.$refs.paymentsPanel && this.$refs.paymentsPanel.resetForm) {
          this.$refs.paymentsPanel.resetForm();
        }
        await this.applyDocument(data);
        this.$store.dispatch(SHOW_SNACK, {
          message: this.$t("GENERAL.SUCCESS_MESSAGE"),
          color: "success",
        });
      } catch (errors) {
        this.$store.dispatch(SHOW_SNACK, {
          message: (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      } finally {
        this.isProcessing = false;
      }
    },
    async cancelPayment(payment) {
      if (!payment || !payment.id || payment.is_cancelled) {
        return;
      }
      const confirmKey = this.document.type === "credit_note"
        ? "STOCK.INVOICES.ANNUL_REFUND_CONFIRM"
        : "STOCK.INVOICES.ANNUL_PAYMENT_CONFIRM";
      if (!window.confirm(this.$t(confirmKey))) {
        return;
      }
      this.isProcessing = true;
      try {
        const data = await this.$store.dispatch(CANCEL_STOCK_INVOICE_PAYMENT, {
          id: this.$route.params.id,
          paymentId: payment.id,
        });
        await this.applyDocument(data);
        this.$store.dispatch(SHOW_SNACK, {
          message: this.$t("GENERAL.SUCCESS_MESSAGE"),
          color: "success",
        });
      } catch (errors) {
        this.$store.dispatch(SHOW_SNACK, {
          message: (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      } finally {
        this.isProcessing = false;
      }
    },
    async mergeLines() {
      this.isProcessing = true;
      try {
        if (this.canSave) {
          this.validationErrors = {};
          const data = await this.$store.dispatch(UPDATE_STOCK_INVOICE, {
            id: this.$route.params.id,
            params: this.payload(),
          });
          await this.applyDocument(data);
        }
        const data = await this.$store.dispatch(MERGE_STOCK_INVOICE_LINES, this.$route.params.id);
        await this.applyDocument(data);
        this.$store.dispatch(SHOW_SNACK, {
          message: this.$t("STOCK.INVOICES.MERGE_SUCCESS"),
          color: "success",
        });
      } catch (errors) {
        this.validationErrors = (errors && errors.errors) || {};
        this.$store.dispatch(SHOW_SNACK, {
          message: (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      } finally {
        this.isProcessing = false;
      }
    },
    async loadAddableDeliveryNotes() {
      if (this.isNew || !this.document.company_id) {
        this.deliveryNoteOptions = [];
        return;
      }
      this.loadingDeliveryNotes = true;
      try {
        const linked = new Set((this.document.delivery_notes || []).map((note) => Number(note.id)));
        const incoming = this.document.direction === "incoming";
        const response = await this.$store.dispatch(GET_STOCK_DELIVERY_NOTES, {
          page: 1,
          itemsPerPage: 50,
          search: this.deliveryNoteSearch || "",
          company_id: this.document.company_id,
          direction: incoming ? "inbound" : "outbound",
          status: incoming ? ["confirmed", "executed"] : ["executed"],
        });
        this.deliveryNoteOptions = (response.data || [])
          .filter((note) => !linked.has(Number(note.id)))
          .map((note) => ({
            id: note.id,
            label: `${note.code} · ${note.order_code || ""}`,
          }));
      } finally {
        this.loadingDeliveryNotes = false;
      }
    },
    async addDeliveryNote(id) {
      if (!id || !this.$route.params.id) {
        return;
      }
      this.isProcessing = true;
      try {
        const data = await this.$store.dispatch(ADD_STOCK_INVOICE_DELIVERY_NOTES, {
          id: this.$route.params.id,
          deliveryNoteIds: [Number(id)],
        });
        await this.applyDocument(data);
        this.addDeliveryNoteId = null;
        await this.loadAddableDeliveryNotes();
        this.$store.dispatch(SHOW_SNACK, {
          message: this.$t("GENERAL.SUCCESS_MESSAGE"),
          color: "success",
        });
      } catch (errors) {
        this.$store.dispatch(SHOW_SNACK, {
          message: (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      } finally {
        this.isProcessing = false;
      }
    },
    async removeDeliveryNote(note) {
      if (!note || !this.$route.params.id) {
        return;
      }
      this.removingDeliveryNoteId = note.id;
      try {
        const data = await this.$store.dispatch(REMOVE_STOCK_INVOICE_DELIVERY_NOTES, {
          id: this.$route.params.id,
          deliveryNoteIds: [Number(note.id)],
        });
        await this.applyDocument(data);
        await this.loadAddableDeliveryNotes();
        this.$store.dispatch(SHOW_SNACK, {
          message: this.$t("STOCK.INVOICES.DELIVERY_NOTE_REMOVED"),
          color: "success",
        });
      } catch (errors) {
        this.$store.dispatch(SHOW_SNACK, {
          message: (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      } finally {
        this.removingDeliveryNoteId = null;
      }
    },
    async removeSourceAction(action) {
      if (!action || !this.$route.params.id) {
        return;
      }

      this.removingSourceActionId = action.id;
      try {
        const data = await this.$store.dispatch(REMOVE_STOCK_INVOICE_ACTIONS, {
          id: this.$route.params.id,
          params: { stock_action_ids: [Number(action.id)] },
        });
        await this.applyDocument(data);
        this.$store.dispatch(SHOW_SNACK, {
          message: this.$t("STOCK.INVOICES.SOURCE_REMOVED"),
          color: "success",
        });
      } catch (errors) {
        this.$store.dispatch(SHOW_SNACK, {
          message: (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      } finally {
        this.removingSourceActionId = null;
      }
    },
    async convertProformaToInvoice() {
      this.isProcessing = true;
      try {
        const data = await this.$store.dispatch(CREATE_INVOICE_FROM_PROFORMA, {
          id: this.$route.params.id,
        });
        this.$router.push({ name: "stock-invoices-view", params: { id: data.id } });
      } catch (errors) {
        this.$store.dispatch(SHOW_SNACK, {
          message: (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      } finally {
        this.isProcessing = false;
      }
    },
    cloneInvoice() {
      if (this.isIncoming) {
        this.cloneForm.external_number = "";
        this.cloneValidationErrors = {};
        this.showCloneDialog = true;
        return;
      }
      this.submitCloneInvoice();
    },
    closeCloneDialog() {
      if (this.isProcessing) {
        return;
      }
      this.showCloneDialog = false;
      this.cloneForm.external_number = "";
      this.cloneValidationErrors = {};
    },
    confirmCloneInvoice() {
      if (!this.canSubmitClone) {
        return;
      }
      this.submitCloneInvoice({
        external_number: this.cloneForm.external_number.trim(),
      });
    },
    async submitCloneInvoice(params = {}) {
      this.isProcessing = true;
      this.cloneValidationErrors = {};
      try {
        const data = await this.$store.dispatch(CLONE_STOCK_INVOICE, {
          id: this.$route.params.id,
          params,
        });
        this.showCloneDialog = false;
        this.$store.dispatch(SHOW_SNACK, {
          message: this.$t("STOCK.INVOICES.CLONE_SUCCESS"),
          color: "success",
        });
        this.$router.push({ name: "stock-invoices-view", params: { id: data.id } });
      } catch (errors) {
        this.cloneValidationErrors = (errors && errors.errors) || {};
        this.$store.dispatch(SHOW_SNACK, {
          message: (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      } finally {
        this.isProcessing = false;
      }
    },
    createCreditNote() {
      if (this.isIncoming) {
        this.creditNoteForm.external_number = "";
        this.creditNoteValidationErrors = {};
        this.showCreditNoteDialog = true;
        return;
      }
      this.submitCreateCreditNote();
    },
    closeCreditNoteDialog() {
      if (this.isProcessing) {
        return;
      }
      this.showCreditNoteDialog = false;
      this.creditNoteForm.external_number = "";
      this.creditNoteValidationErrors = {};
    },
    confirmCreateCreditNote() {
      if (!this.canSubmitCreditNote) {
        return;
      }
      this.submitCreateCreditNote({
        external_number: this.creditNoteForm.external_number.trim(),
      });
    },
    async submitCreateCreditNote(params = {}) {
      this.isProcessing = true;
      this.creditNoteValidationErrors = {};
      try {
        const data = await this.$store.dispatch(CREATE_STOCK_CREDIT_NOTE, {
          id: this.$route.params.id,
          params,
        });
        this.showCreditNoteDialog = false;
        this.$router.push({ name: "stock-invoices-view", params: { id: data.id } });
      } catch (errors) {
        this.creditNoteValidationErrors = (errors && errors.errors) || {};
        this.$store.dispatch(SHOW_SNACK, {
          message: (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      } finally {
        this.isProcessing = false;
      }
    },
    async regeneratePdf() {
      this.isProcessing = true;
      try {
        const data = await this.$store.dispatch(
          REGENERATE_STOCK_INVOICE_DOCUMENT,
          this.$route.params.id
        );
        await this.applyDocument(data);
        this.$store.dispatch(SHOW_SNACK, {
          message: this.$t("GENERAL.SUCCESS_MESSAGE"),
          color: "success",
        });
      } catch (errors) {
        this.$store.dispatch(SHOW_SNACK, {
          message: (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      } finally {
        this.isProcessing = false;
      }
    },
    openEmailDialog(kind) {
      this.emailForm = {
        kind,
        message_note: "",
      };
      this.showEmailDialog = true;
    },
    closeEmailDialog() {
      if (this.isProcessing) {
        return;
      }
      this.showEmailDialog = false;
    },
    async confirmEmailSend() {
      if (!this.canConfirmEmailSend) {
        return;
      }
      const kind = this.emailForm.kind;
      const messageNote = this.emailForm.message_note || null;

      if (this.isDirty && !this.canSave) {
        this.$store.dispatch(SHOW_SNACK, {
          message: this.$t("STOCK.INVOICES.EMAIL_SAVE_REQUIRED"),
          color: "error",
        });
        return;
      }

      if (this.canSave) {
        const saved = await this.save({ silent: true });
        if (!saved) {
          this.showEmailDialog = false;
          return;
        }
      }

      this.isProcessing = true;
      try {
        const data = await this.$store.dispatch(RECORD_STOCK_INVOICE_EMAIL_SEND, {
          id: this.$route.params.id,
          params: {
            kind,
            message_note: messageNote,
          },
        });
        await this.applyDocument(data);
        this.showEmailDialog = false;
        this.$store.dispatch(SHOW_SNACK, {
          message: this.$t("STOCK.INVOICES.EMAIL_RECORDED"),
          color: "success",
        });
      } catch (errors) {
        const bag = errors && errors.errors;
        const first = bag ? Object.values(bag).flat()[0] : null;
        this.$store.dispatch(SHOW_SNACK, {
          message: first || (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      } finally {
        this.isProcessing = false;
      }
    },
    downloadEmailSnapshot(send) {
      const name = (send.snapshot && send.snapshot.original_name) || "invoice.pdf";
      this.$store.dispatch(DOWNLOAD_STOCK_INVOICE_EMAIL_SEND, {
        id: this.$route.params.id,
        sendId: send.id,
        originalName: name,
      });
    },
    downloadInvoicePdf() {
      const file = this.generatedInvoicePdf;
      if (!file?.id) {
        return;
      }
      this.$store
        .dispatch(DOWNLOAD_STOCK_INVOICE_FILE, {
          id: this.$route.params.id,
          fileId: file.id,
          originalName: file.original_name,
          name: file.name,
        })
        .catch((errors) => {
          this.$store.dispatch(SHOW_SNACK, {
            message: (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
            color: "error",
          });
        });
    },
    onFilesUpdated(files) {
      this.documentFiles = files || [];
    },
  },
};
</script>

<style scoped>
.stock-invoice-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 320px;
  border-radius: 16px;
  background: #fafafa;
  border: 1px solid rgba(0, 0, 0, 0.06);
}
.stock-invoice-loading__label {
  margin-top: 16px;
  color: rgba(0, 0, 0, 0.54);
  font-size: 0.95rem;
}
.stock-invoice-totals-sticky {
  position: sticky;
  top: 80px;
}
.stock-invoice-hero__action-row {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
}
.stock-invoice-direction-card {
  cursor: pointer;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.15s ease;
  border-width: 2px !important;
}
.stock-invoice-direction-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}
.stock-invoice-direction-card--active {
  border-color: #00897b !important;
  box-shadow: 0 4px 16px rgba(0, 137, 123, 0.2);
}
.stock-invoice-section {
  border: 1px solid #e6e6e6;
  border-left: 3px solid #00897b;
  border-radius: 10px;
  background: #fff;
  padding: 12px 14px 4px;
  margin-bottom: 14px;
}
.stock-invoice-section__head {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 8px;
}
.stock-invoice-section__title {
  font-size: 14px;
  font-weight: 600;
  line-height: 1.3;
}
.stock-invoice-section__hint {
  font-size: 12px;
  line-height: 1.35;
  color: #757575;
}
.stock-invoice-reverse-charge__hint {
  margin: -2px 0 8px 32px;
  font-size: 12px;
  line-height: 1.35;
  color: #757575;
}
.stock-invoice-reverse-charge__hint--warn {
  color: #e65100;
}
.stock-invoice-gfsi-note__hint {
  margin: 0 0 8px 32px;
  font-size: 12px;
  line-height: 1.35;
  color: #757575;
}
.stock-invoice-party-col + .stock-invoice-party-col {
  margin-top: 8px;
}
@media (min-width: 960px) {
  .stock-invoice-party-col + .stock-invoice-party-col {
    margin-top: 0;
    border-left: 1px solid #eeeeee;
  }
}
</style>

<style>
.stock-invoice-status--draft {
  background-color: #757575 !important;
}
.stock-invoice-status--issued {
  background-color: #00897b !important;
}
.stock-invoice-status--partially_paid {
  background-color: #ef6c00 !important;
}
.stock-invoice-status--paid {
  background-color: #2e7d32 !important;
}
.stock-invoice-status--cancelled {
  background-color: #c62828 !important;
}
</style>
